import { useRouter } from "next/navigation";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import { DestinationEdit } from "@/config/app-constant";
import useGtmCustomEvent from "@/hooks/gtm/use-gtm-custom-events";
import { useRenewalStateContext } from "@/providers/renewal-state-provider";
import { RootState } from "@/store/root-reducer";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { QuotePayload } from "@/types/quote/quote-data";
import { modifyQuestionQuotePayload } from "@/utils/common.utils";
import { TripArea } from "@/utils/mocks/quote/common-data/common-data";

type Region = {
  regionArea: string;
  regions: string;
  areaCode: TripArea;
};

type Regions = {
  [key: string]: Region;
};

type TravelRegionProps = {
  cmsData: {
    travelRegionTitle: string;
    travelRegionEditButtonLabel: string;
    regions: Regions | undefined;
  };
};

function TravelRegion({ cmsData }: Readonly<TravelRegionProps>) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { travelRegionTitle, travelRegionEditButtonLabel, regions } = cmsData;
  const quote = useSelector((state: RootState) => state.quote);
  const { tripDetails } = quote.attributes;

  const { updateLastEditAccordion } = useRenewalStateContext();

  const handleGtmEvent = useGtmCustomEvent({
    event: "custom_events",
    eventName: "edit_cta",
    quoteSection: "policy details",
  });

  const region = useMemo(() => {
    if (!regions) {
      return undefined;
    }
    const regionData = Object.values(regions).find(
      (item) => item.areaCode === tripDetails?.area
    );
    return regionData as Region;
  }, [regions, tripDetails?.area]);

  return (
    <div id="travel-region-section">
      <p className="font-body-font text-2xs font-[400] leading-[24px] text-text-functional-subtle">
        {travelRegionTitle}
      </p>
      <div className="flex justify-between items-start">
        <div>
          <p className="font-body-font text-xs font-[700] leading-[27px] text-interactive-secondary-default">
            {region?.regionArea}
          </p>
          <p className="font-body-font text-xs font-[400] leading-[27px] text-interactive-secondary-default">
            {region?.regions}
          </p>
        </div>

        <div>
          <LabelButtonWrapper
            id="policy-accordion-travel-region-edit-button"
            label={travelRegionEditButtonLabel}
            hierarchy="tertiary"
            className="font-body-font leading-[18px] font-[600] text-xs py-spacing-xxs realtive top-[-6px] !px-spacing-xxs md:ml-spacing-m"
            onClick={() => {
              updateLastEditAccordion("policy-details");
              dispatch(
                setQuestionData(
                  modifyQuestionQuotePayload(quote as QuotePayload)
                )
              );
              handleGtmEvent();
              router.push(`/travelling-to?${DestinationEdit}=true`);
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default TravelRegion;
